import { Dispatch, SetStateAction } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { UserType } from 'hooks/useUserData';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import ProfileDefaultFields from './ProfileDefaultFields';
import ProfileAvatar from '../Details/ProfileAvatar';
import ProfileCustomFields from './ProfileCustomFields';
import { useProfileForm } from '../../hooks';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import AlertModal from 'components/utils/AlertModal';

type EditProfileModalProps = {
  showAsWelcomeBanner?: boolean;
  open: boolean;
  user: UserType;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function EditProfileModal({
  showAsWelcomeBanner = false,
  open,
  user,
  setOpen,
}: EditProfileModalProps) {
  const {
    customInputFields,
    isCustomFieldsFetched,
    isSubmitting,
    profileForm,
    openConfirmation,
    handleClose,
    setOpenConfirmation,
    closeModal,
    onSubmit,
  } = useProfileForm({
    user,
    setOpen,
  });

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        TransitionComponent={GrowDialogTransition}
      >
        <CustomDialogTitle
          id="dialog-title"
          {...(!showAsWelcomeBanner && { onClose: handleClose })}
        >
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'black' }}>
            {showAsWelcomeBanner ? 'Welcome to BuildWise' : 'Edit Profile'}
          </Typography>
        </CustomDialogTitle>
        <DialogContent dividers id="dialog-description">
          {showAsWelcomeBanner && <Note>Let's complete your profile.</Note>}
          <form onSubmit={onSubmit}>
            <FormProvider {...profileForm}>
              <Box
                mb={3}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <ProfileAvatar user={user} isLoggedInUser />
                {user.userNameToDisplay && <UserName>{user.userNameToDisplay}!</UserName>}
              </Box>
              <ProfileDefaultFields />
              <Collapse in={isCustomFieldsFetched}>
                <ProfileCustomFields fields={customInputFields} />
              </Collapse>
            </FormProvider>
          </form>
        </DialogContent>
        <DialogActions>
          <Button type="submit" disabled={isSubmitting} onClick={onSubmit} variant="contained">
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : showAsWelcomeBanner ? (
              'Get Started'
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertModal
        open={openConfirmation}
        setOpen={setOpenConfirmation}
        handleCancel={() => {
          closeModal();
        }}
        onSubmit={onSubmit}
        loading={isSubmitting}
        title="Confirm"
        submitButtonName={'Save Changes'}
        cancelButtonName="Discard"
        description={'Are you sure you want to discard your changes?'}
        discardActions
      />
    </>
  );
}

const UserName = styled(Typography)(({ theme }) => ({
  marginTop: 8,
  color: theme.palette.custom.tuna,
  fontSize: 22,
  fontWeight: 700,
}));

const Note = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.dusk,
  fontSize: 16,
  marginBottom: 8,
}));

export default EditProfileModal;
